import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api";

interface Detail {
  subscription: {
    id: string;
    familyId: string;
    childId: string;
    conversationId: string | null;
    tier: "basic" | "advanced" | "premium";
    annualPriceInr: number;
    status: string;
    startsAt: string;
    expiresAt: string;
    razorpayPaymentLinkId: string | null;
    razorpaySubscriptionId: string | null;
    closedByUserEmail: string;
    notes: string | null;
    createdAt: string;
  };
  payments: Array<{
    id: string;
    razorpayPaymentId: string | null;
    razorpayEvent: string;
    amountInrPaise: number;
    status: string;
    method: string | null;
    occurredAt: string;
  }>;
  conversation: { id: string; outcome: string | null; recommendedTier: string | null } | null;
  child: { id: string; fullName: string; dob: string } | null;
  family: { id: string; city: string | null } | null;
}

function SubscriptionDetail() {
  const { subscriptionId } = Route.useParams();
  const [d, setD] = useState<Detail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<Detail>(`/api/subscriptions/${subscriptionId}`)
      .then(setD)
      .catch((e: Error) => setErr(e.message));
  }, [subscriptionId]);

  if (err) return <div className="panel text-red-600">{err}</div>;
  if (!d) return <div className="panel text-slate-500">Loading…</div>;

  const s = d.subscription;
  return (
    <div className="space-y-3">
      <div className="panel">
        <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Subscription</div>
        <h1 className="text-2xl font-semibold mb-2 capitalize">{s.tier}</h1>
        <div className="grid grid-cols-3 gap-3 text-sm mb-3">
          <div>
            <div className="text-xs text-slate-500">Status</div>
            <span
              className={`rounded text-xs px-1.5 ${
                s.status === "active"
                  ? "bg-green-100 text-green-800"
                  : s.status === "lapsed"
                    ? "bg-red-100 text-red-800"
                    : "bg-amber-100 text-amber-800"
              }`}
            >
              {s.status}
            </span>
          </div>
          <div>
            <div className="text-xs text-slate-500">Annual</div>
            ₹{s.annualPriceInr.toLocaleString("en-IN")}
          </div>
          <div>
            <div className="text-xs text-slate-500">Closed by</div>
            {s.closedByUserEmail}
          </div>
          <div>
            <div className="text-xs text-slate-500">Starts</div>
            {new Date(s.startsAt).toLocaleDateString()}
          </div>
          <div>
            <div className="text-xs text-slate-500">Expires</div>
            {new Date(s.expiresAt).toLocaleDateString()}
          </div>
          <div>
            <div className="text-xs text-slate-500">Razorpay link id</div>
            <code className="text-xs">{s.razorpayPaymentLinkId ?? "—"}</code>
          </div>
        </div>
        <div className="text-xs text-slate-500 border-t pt-2">
          Family:{" "}
          <Link
            to="/family/$familyId"
            params={{ familyId: s.familyId }}
            className="text-slate-700 hover:underline"
          >
            {d.family?.city ?? "view"}
          </Link>
          {" · "}
          Child:{" "}
          <span>{d.child?.fullName ?? "—"}</span>
          {d.conversation && (
            <>
              {" · "}
              From conversation outcome:{" "}
              <code className="text-xs">{d.conversation.outcome}</code>
            </>
          )}
        </div>
      </div>

      <div className="panel">
        <h2 className="font-medium text-sm mb-2">
          Payment events ({d.payments.length})
        </h2>
        {d.payments.length === 0 ? (
          <div className="text-sm text-slate-400">No Razorpay events yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 text-left">
              <tr>
                <th className="py-1">When</th>
                <th>Event</th>
                <th>Status</th>
                <th>Method</th>
                <th>Amount</th>
                <th>Razorpay ID</th>
              </tr>
            </thead>
            <tbody>
              {d.payments.map((p) => (
                <tr key={p.id} className="border-t border-slate-100">
                  <td className="py-1">{new Date(p.occurredAt).toLocaleString()}</td>
                  <td>
                    <code className="text-xs">{p.razorpayEvent}</code>
                  </td>
                  <td>{p.status}</td>
                  <td>{p.method ?? "—"}</td>
                  <td>₹{(p.amountInrPaise / 100).toLocaleString("en-IN")}</td>
                  <td>
                    <code className="text-xs">{p.razorpayPaymentId ?? "—"}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {s.notes && (
        <div className="panel">
          <h2 className="font-medium text-sm mb-1">Notes</h2>
          <div className="text-sm whitespace-pre-wrap">{s.notes}</div>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/subscriptions/$subscriptionId")({
  component: SubscriptionDetail,
});
